import { useEffect, useState, type CSSProperties } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../components/loader";
import FullPaymentDetails from "../components/payment-page/full-payment-details";
import StripeNonMotoPaymentContainer from "../components/payment-page/stripe-non-moto-payment-container";
import {
  PublicChase,
  brandLogo,
  brandOf,
  isChasePaid,
  paidMessage,
} from "../components/payment-page/payment-page.constants";
import publicAxios from "../utils/publicAxios";
import { ParseErrors } from "../utils/ParseErrors";

const Payment = () => {
  const { policyRef } = useParams();
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get("payment_status");
  // A policy reference can carry more than one chase — the version in the link
  // says which one, and every public call below has to carry it too.
  const version = searchParams.get("version");
  const query = version ? `?version=${encodeURIComponent(version)}` : "";
  const [value, setValue] = useState<{
    chase: PublicChase | null;
    clientSecret: string;
    loading: boolean;
    creating: boolean;
    error: string;
  }>({
    chase: null,
    clientSecret: "",
    loading: true,
    creating: false,
    error: "",
  });
  const set = (patch: Partial<typeof value>) =>
    setValue((s) => ({ ...s, ...patch }));

  // ------------------------------- FIND POLICY [ PUBLIC ] -------------------------------
  useEffect(() => {
    const findChase = async () => {
      try {
        const res = await publicAxios.get(`/pub/chase/${policyRef}${query}`);
        set({ chase: res.data?.data, loading: false });
      } catch (error) {
        set({ error: ParseErrors(error), loading: false });
      }
    };

    findChase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyRef, query]);

  // ------------------- FAILED PAYMENT — STRIPE SENT THE CUSTOMER BACK -------------------
  useEffect(() => {
    if (paymentStatus === "failed") {
      toast.error(
        `Payment failed (${searchParams.get("error_message") || "Unknown error"}) — please try again`,
      );
    }
  }, [paymentStatus, searchParams]);

  const { chase, clientSecret, loading, creating, error } = value;

  // -------- CREATE PAY ORDER [ CASH ] / CONFIRM THE EXISTING PLAN [ INSTALMENTS ] --------
  const handleProceed = async () => {
    set({ creating: true });
    try {
      // Instalments collect nothing — confirming renews on the plan already in place.
      if (chase?.paymentType !== "cash") {
        await publicAxios.post(
          `/pub/payment/instalment/confirm/${policyRef}${query}`,
          {},
        );
        return set({
          chase: { ...chase!, status: "Payment-Received" },
          creating: false,
        });
      }

      const res = await publicAxios.post(
        `/pub/payment/stripe/create/${policyRef}${query}`,
        {},
      );
      set({ clientSecret: res.data?.clientSecret, creating: false });
    } catch (error) {
      toast.error(ParseErrors(error));
      set({ creating: false });
    }
  };

  const isPaid = paymentStatus === "success" || isChasePaid(chase);

  const brand = chase ? brandOf(chase) : null;

  return (
    // The page wears the customer's brand, not Rensure's: every accent below
    // reads these two variables, so both brands theme from one place.
    <div
      className="min-h-screen w-full bg-background flex flex-col items-center px-4 py-10"
      style={
        {
          "--brand": brand?.primary ?? "#6366F1",
          "--brand-soft": `${brand?.primary ?? "#6366F1"}14`,
          "--brand-edge": `${brand?.primary ?? "#6366F1"}33`,
        } as CSSProperties
      }
    >
      <div className="w-full max-w-[560px] rounded-2xl border border-edge bg-surface-elevated shadow-card overflow-hidden animate-fade-in">
        {/* The customer's own brand — the white logo sits on the brand fill. */}
        {chase ? (
          <div
            className="px-6 py-5 flex flex-col items-center gap-2.5"
            style={{ background: brandOf(chase).accentBg }}
          >
            <img
              src={brandLogo(chase)}
              alt={brandOf(chase).name}
              className="h-[36px] max-w-full object-contain"
            />
            <p className="text-white/85 text-[11px] font-semibold uppercase tracking-[1.4px]">
              Policy {chase.policyReferenceNumber}
            </p>
          </div>
        ) : null}

        <div className="p-5 mobile:p-4">
          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader />
            </div>
          ) : error || !chase ? (
            <Message
              icon={faCircleExclamation}
              tone="text-ilara-red"
              title="Policy not found"
              text={
                error ||
                "We couldn't find this policy. Please check the link in your renewal email."
              }
            />
          ) : isPaid ? (
            <Message
              icon={faCircleCheck}
              tone="text-emerald-400"
              {...paidMessage(chase)}
            />
          ) : (
            <>
              <h1 className="text-text-primary text-[22px] font-bold text-center mb-4">
                {clientSecret ? "Payment Details" : "Renew your policy"}
              </h1>

              {clientSecret ? (
                <StripeNonMotoPaymentContainer
                  chase={chase}
                  clientSecret={clientSecret}
                  onBack={() => set({ clientSecret: "" })}
                />
              ) : (
                <FullPaymentDetails
                  chase={chase}
                  loading={creating}
                  onProceed={handleProceed}
                />
              )}
            </>
          )}
        </div>
      </div>

      <p className="text-text-secondary text-xs mt-6 text-center">
        Need help?{" "}
        {brand ? (
          <>
            Call {brand.name} on{" "}
            <span className="font-semibold text-text-primary">
              {brand.callNumber}
            </span>
            .
          </>
        ) : (
          "Reply to your renewal email and our team will get back to you."
        )}
      </p>
    </div>
  );
};

// -------------------------------- FULL CARD MESSAGE --------------------------------
const Message = ({
  icon,
  tone,
  title,
  text,
}: {
  icon: any;
  tone: string;
  title: string;
  text: string;
}) => (
  <div className="py-10 text-center flex flex-col items-center gap-3 animate-fade-in">
    <span className="w-[68px] h-[68px] rounded-full flex items-center justify-center bg-surface-muted border border-edge">
      <FontAwesomeIcon icon={icon} className={`text-[30px] ${tone}`} />
    </span>
    <h1 className="text-text-primary text-[22px] font-bold">{title}</h1>
    <p className="text-text-secondary text-sm max-w-[380px] leading-relaxed">
      {text}
    </p>
  </div>
);

export default Payment;
